import type { LoaderFunctionArgs } from "react-router";
import { redirect, Form, useLoaderData } from "react-router";

import { login } from "../../shopify.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);

  if (url.searchParams.get("shop")) {
    throw redirect(`/app?${url.searchParams.toString()}`);
  }

  return { showForm: Boolean(login) };
};

export default function App() {
  const { showForm } = useLoaderData<typeof loader>();

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-text">
            <h1 className="landing-hero-heading">Boost Your Average Order Value with SmashCart</h1>
            <p className="landing-hero-subheading">
              A smart shopping cart that increases conversions with AI-powered upsells, cross-sells, and a seamless checkout experience.
            </p>
            {showForm && (
              <Form className="landing-install-form" method="post" action="/auth/login">
                <input
                  className="landing-shop-input"
                  type="text"
                  name="shop"
                  placeholder="your-store.myshopify.com"
                />
                <button className="landing-cta-button" type="submit">
                  Install Now - Free Trial
                </button>
              </Form>
            )}
            <div className="landing-trust-badges">
              <span>⚡ 5-minute setup</span>
              <span>💳 No credit card required</span>
              <span>✨ 14-day free trial</span>
            </div>
          </div>
          <div className="landing-hero-image">
            <div className="landing-hero-placeholder">
              {/* Placeholder for hero image/demo */}
              <div className="landing-mock-cart">
                <div className="landing-mock-cart-header">SmashCart Demo</div>
                <div className="landing-mock-cart-content">
                  <div className="landing-mock-item"></div>
                  <div className="landing-mock-item"></div>
                  <div className="landing-mock-upsell">+ Upsell Offer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="landing-features">
        <div className="landing-section-header">
          <h2 className="landing-section-title">Everything You Need to Maximize Cart Value</h2>
          <p className="landing-section-subtitle">Powerful features designed to increase your average order value</p>
        </div>
        <div className="landing-feature-grid">
          <div className="landing-feature-card">
            <div className="landing-feature-icon">🎯</div>
            <h3 className="landing-feature-title">Smart Upsells & Cross-Sells</h3>
            <p className="landing-feature-text">
              AI-powered product recommendations that appear at the perfect moment to increase order value.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">🎨</div>
            <h3 className="landing-feature-title">Fully Customizable Design</h3>
            <p className="landing-feature-text">
              Match your brand perfectly with our intuitive design editor. No coding required.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">⚡</div>
            <h3 className="landing-feature-title">Lightning Fast Performance</h3>
            <p className="landing-feature-text">
              Optimized for speed to ensure your cart loads instantly and never slows down your store.
            </p>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-icon">📊</div>
            <h3 className="landing-feature-title">Analytics Dashboard</h3>
            <p className="landing-feature-text">
              Track cart performance, conversion rates, and revenue in real-time with detailed insights.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="landing-pricing">
        <div className="landing-section-header">
          <h2 className="landing-section-title">Simple, Transparent Pricing</h2>
          <p className="landing-section-subtitle">Choose the plan that fits your business</p>
        </div>
        <div className="landing-pricing-grid">
          <div className="landing-pricing-card">
            <h3 className="landing-pricing-plan">Starter</h3>
            <div className="landing-pricing-price">
              <span className="landing-price-amount">$29</span>
              <span className="landing-price-period">/month</span>
            </div>
            <ul className="landing-pricing-features">
              <li>✓ Basic upsells</li>
              <li>✓ Custom design</li>
              <li>✓ Email support</li>
              <li>✓ Up to 1,000 orders/mo</li>
            </ul>
            <button className="landing-pricing-button">Start Free Trial</button>
          </div>
          <div className="landing-pricing-card landing-pricing-card-featured">
            <div className="landing-popular-badge">Most Popular</div>
            <h3 className="landing-pricing-plan">Professional</h3>
            <div className="landing-pricing-price">
              <span className="landing-price-amount">$79</span>
              <span className="landing-price-period">/month</span>
            </div>
            <ul className="landing-pricing-features">
              <li>✓ Advanced AI upsells</li>
              <li>✓ Priority support</li>
              <li>✓ Advanced analytics</li>
              <li>✓ Unlimited orders</li>
            </ul>
            <button className="landing-pricing-button landing-pricing-button-primary">Start Free Trial</button>
          </div>
          <div className="landing-pricing-card">
            <h3 className="landing-pricing-plan">Enterprise</h3>
            <div className="landing-pricing-price">
              <span className="landing-price-amount">Custom</span>
            </div>
            <ul className="landing-pricing-features">
              <li>✓ Everything in Pro</li>
              <li>✓ Dedicated support</li>
              <li>✓ Custom integrations</li>
              <li>✓ SLA guarantees</li>
            </ul>
            <button className="landing-pricing-button">Contact Sales</button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="landing-faq">
        <div className="landing-section-header">
          <h2 className="landing-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="landing-faq-grid">
          <div className="landing-faq-item">
            <h3 className="landing-faq-question">How long does installation take?</h3>
            <p className="landing-faq-answer">
              SmashCart installs in under 5 minutes. Simply connect your Shopify store and our wizard guides you through the setup.
            </p>
          </div>
          <div className="landing-faq-item">
            <h3 className="landing-faq-question">Can I customize the cart design?</h3>
            <p className="landing-faq-answer">
              Yes! SmashCart is fully customizable with our visual editor. Match your brand colors, fonts, and layout without any coding.
            </p>
          </div>
          <div className="landing-faq-item">
            <h3 className="landing-faq-question">Is there a free trial?</h3>
            <p className="landing-faq-answer">
              Absolutely! Try SmashCart free for 14 days. No credit card required. Cancel anytime.
            </p>
          </div>
          <div className="landing-faq-item">
            <h3 className="landing-faq-question">What support do you offer?</h3>
            <p className="landing-faq-answer">
              All plans include email support. Pro and Enterprise plans get priority support with faster response times.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-final-cta">
        <div className="landing-cta-content">
          <h2 className="landing-cta-heading">Ready to Boost Your Sales?</h2>
          <p className="landing-cta-text">
            Join thousands of Shopify stores using SmashCart to increase their average order value.
          </p>
          {showForm && (
            <Form className="landing-install-form" method="post" action="/auth/login">
              <input
                className="landing-shop-input"
                type="text"
                name="shop"
                placeholder="your-store.myshopify.com"
              />
              <button className="landing-cta-button" type="submit">
                Start Free Trial
              </button>
            </Form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-brand">
            <h3 className="landing-footer-logo">SMASH CART</h3>
            <p className="landing-footer-tagline">Supercharge your Shopify cart</p>
          </div>
          <div className="landing-footer-links">
            <div className="landing-footer-column">
              <h4 className="landing-footer-column-title">Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#docs">Documentation</a>
            </div>
            <div className="landing-footer-column">
              <h4 className="landing-footer-column-title">Support</h4>
              <a href="#help">Help Center</a>
              <a href="#contact">Contact Us</a>
              <a href="#status">System Status</a>
            </div>
            <div className="landing-footer-column">
              <h4 className="landing-footer-column-title">Legal</h4>
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <p>© 2026 SmashCart. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
